import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, AlertTriangle, Download } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { exportListPDF } from '../../lib/pdfExport';
import { useSortable } from '../../lib/useSortable';
import { SortableTh } from '../../components/SortableTh';

const handleRowKeyDown = (e, cb) => { if (e.key === 'Enter' || e.key === ' ') cb(); };

const STATUS_TABS = [
  { key: 'outstanding',    label: 'Outstanding' },
  { key: 'partially_paid', label: 'Part Paid' },
  { key: 'overdue',        label: 'Overdue' },
  { key: 'paid',           label: 'Paid' },
  { key: '',               label: 'All' },
  { key: 'draft',          label: 'Draft' },
  { key: 'voided',         label: 'Void' },
];

const fmt = (n, c) => {
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c || 'USD' }).format(n); }
  catch { return `${c} ${n}`; }
};

const STATUS_STYLE = {
  outstanding:            'bg-blue-50 text-blue-700 border-blue-200',
  overdue:                'bg-red-50 text-red-700 border-red-200',
  partially_paid:         'bg-amber-50 text-amber-700 border-amber-200',
  partially_paid_overdue: 'bg-orange-50 text-orange-700 border-orange-200',
  paid:                   'bg-emerald-50 text-emerald-700 border-emerald-200',
  draft:                  'bg-slate-100 text-slate-600 border-slate-200',
  voided:                 'bg-slate-100 text-slate-400 border-slate-200',
};

export default function InvoicesList() {
  const { canWrite } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('outstanding');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clientId = searchParams.get('client_id');
  const { sorted, sortKey, sortDir, onSort } = useSortable(invoices, 'issue_date', 'desc');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (clientId) params.set('client_id', clientId);
    api.get(`/api/invoices?${params}`).then(r => setInvoices(r.data)).finally(() => setLoading(false));
  }, [status, clientId]);

  const handleExport = () => {
    exportListPDF('Invoices', status ? `Status: ${status}` : 'All invoices', [
      { label: 'Invoice #', key: 'invoice_number' },
      { label: 'Client', key: 'client_name' },
      { label: 'Issue Date', key: 'issue_date' },
      { label: 'Due Date', key: 'due_date' },
      { label: 'Currency', key: 'currency_code' },
      { label: 'Total', key: 'total_amount' },
      { label: 'Balance Due', key: 'balance_due_amount_placeholder' },
      { label: 'Status', key: 'status' },
    ], invoices);
  };

  return (
    <div className="p-6 fade-in" data-testid="invoices-list-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Invoices</h1>
          <p className="text-sm text-slate-500 mt-0.5">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} disabled={invoices.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-md hover:bg-slate-50 disabled:opacity-40"
            data-testid="export-invoices-pdf-btn">
            <Download size={14} /> Export PDF
          </button>
          {canWrite() && (
            <Link
              to="/invoices/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 transition-colors"
              data-testid="new-invoice-button"
            >
              <Plus size={15} /> New Invoice
            </Link>
          )}
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="invoices-status-tabs">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatus(tab.key)}
            data-testid={`status-tab-${tab.key || 'all'}`}
            className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 transition-colors -mb-px ${
              status === tab.key
                ? 'bg-white border-slate-200 text-indigo-900 font-semibold'
                : 'bg-slate-50 border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm table-mono" data-testid="invoices-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <SortableTh label="Invoice #" col="invoice_number"              sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Client"    col="client_name"                 sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Issue Date" col="issue_date"                 sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Due Date"  col="due_date"                    sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Total"     col="total_amount"                sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortableTh label="Balance"   col="balance_due_amount_placeholder" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortableTh label="Status"    col="status"                      sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <tr key={i}>{[...Array(7)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>
              ))
            ) : invoices.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                No {status || ''} invoices found
              </td></tr>
            ) : (
              sorted.map(inv => (
                <tr
                  key={inv.invoice_id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                  data-testid={`invoice-row-${inv.invoice_id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs font-medium text-slate-900">
                    <Link to={`/invoices/${inv.invoice_id}`} className="flex items-center gap-1.5 hover:text-indigo-700" onClick={e => e.stopPropagation()}>
                      {inv.status === 'overdue' && <AlertTriangle size={12} className="text-red-500" />}
                      {inv.invoice_number || <span className="text-slate-400 italic">— draft —</span>}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{inv.client_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{inv.issue_date}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{inv.due_date}</td>
                  <td className="px-4 py-3 text-right amount font-mono">{fmt(inv.total_amount, inv.currency_code)}</td>
                  <td className={`px-4 py-3 text-right amount font-mono font-semibold ${inv.balance_due_amount_placeholder > 0 && inv.status !== 'paid' ? 'text-red-600' : 'text-green-600'}`}>
                    {fmt(inv.balance_due_amount_placeholder, inv.currency_code)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLE[inv.status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                      {inv.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
